// useBackNavigationHandler.ts

import { PaymentType } from 'Modulor/CurrencySelectContainer/types';
import { useNavigateWithSearch } from 'Modulor/hooks';
import { Routes } from 'Modulor/routes';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import IntegrationCommunication from 'services/integration-communication';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  isCustomIframeModeSelector,
  isInPopUpSelector,
  resetNavigationKey,
  setStatusPageCleanupRequired,
} from 'store/slices/global';
import { redirectSelector, redirectTypeSelector } from 'store/slices/redirect';
import { activeTabSelector } from 'store/slices/tabs';
import { currencyFlowSelector } from 'store/slices/wallet';
import { blurActiveElement, getWidgetLocation, handleRedirect, inIframe } from 'utils';
import { internalNavigateBack } from 'utils/navigationControl';

import { useResetCategoryAutoSelect } from '../useResetCategoryAutoSelect';

export const useBackNavigationHandler = () => {
  const navigate = useNavigateWithSearch();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector(activeTabSelector);
  const currencyFlow = useAppSelector(currencyFlowSelector);
  const isCustomIframeMode = useAppSelector(isCustomIframeModeSelector);
  const redirectData = useAppSelector(redirectSelector);
  const redirectType = useAppSelector(redirectTypeSelector);
  const { isDisplayForm, isDepositForm, isWithdrawalForm, isErrorPage, isSuccessPage, isProcessingPage, isMainPage } =
    getWidgetLocation(location.pathname);
  const isCryptoFlow = currencyFlow === 'crypto' || activeTab === PaymentType.CRYPTO;
  const isInWeb = inIframe();
  const isOpenedInPopup = useAppSelector(isInPopUpSelector);
  const resetCategoryAutoSelect = useResetCategoryAutoSelect();

  const handleBack = useCallback(() => {
    console.log('handleBack', location.pathname);
    resetCategoryAutoSelect();
    const { merchantUrl } = redirectData;
    if (isErrorPage) {
      handleRedirect({
        urlToRedirect: redirectData.failUrl || merchantUrl,
        isOpenedInPopup,
        merchantRedirectType: redirectType || '',
      });
      return;
    }

    if (isSuccessPage) {
      handleRedirect({
        urlToRedirect: redirectData.successUrl || redirectData.merchantUrl,
        isOpenedInPopup,
        merchantRedirectType: redirectType || '',
      });
      return;
    }

    if (isProcessingPage) {
      handleRedirect({
        urlToRedirect: redirectData.processingUrl || merchantUrl,
        isOpenedInPopup,
        merchantRedirectType: redirectType || '',
      });
      return;
    }

    if (isDisplayForm && isCryptoFlow) {
      handleRedirect({
        urlToRedirect: merchantUrl,
        isOpenedInPopup,
        merchantRedirectType: redirectType || '',
      });
      return;
    }

    if (isDisplayForm || isDepositForm) {
      navigate(Routes.deposit);
      return;
    }

    if (isWithdrawalForm) {
      navigate(Routes.withdrawal);
      return;
    }

    const previousPath = location.state?.from;

    if (previousPath) {
      navigate(previousPath);
      return;
    }

    // Default fallback
    internalNavigateBack(navigate);
  }, [
    navigate,
    internalNavigateBack,
    isCryptoFlow,
    isDisplayForm,
    isDepositForm,
    isWithdrawalForm,
    isErrorPage,
    isSuccessPage,
    isProcessingPage,
    redirectData,
    isOpenedInPopup,
    redirectType,
    isMainPage,
  ]);

  const handleRestore = useCallback(
    (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      // 1. UI FIX: remove focus from form (bfcache)
      blurActiveElement();

      // If the page was restored from cache (browser back/forward navigation)
      if (!isCustomIframeMode && (isInWeb || isOpenedInPopup)) {
        IntegrationCommunication?.sendMessage({
          id: 'reInitCashier',
        });
      }
      dispatch(setStatusPageCleanupRequired(true));
      dispatch(resetNavigationKey());
    },
    [isCustomIframeMode, isInWeb, dispatch, isOpenedInPopup],
  );

  return {
    handleBack,
    handleRestore,
  };
};
